import React, { useState } from 'react';
import { FormContext, useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import CircularProgress from '@material-ui/core/CircularProgress';
import {
  DEFAULT_MAX_ATTACHMENTS_SIZE,
  getAttachmentsSizeValidationError
} from 'features/sendouts/common/utils';
import { useSendoutCreationAccountability } from 'features/sendouts/hooks';
import { useSendoutCandidateHydration } from 'features/sendouts/hooks/useSendoutCandidateHydration';
import { useFetchWithStatusV2 } from 'hooks/fetchWithStatus';
import { useUserContext } from 'providers/UserProvider';
import { EntityRoutes } from 'routes/constants';
import { userHasSomeRoles } from 'services/Authorization';
import strings from 'strings';
import { FPActionButton, When } from 'UI/components/atoms';
import FPButtonBuilder from 'UI/components/molecules/FPButtonBuilder';
import HighlightBox from 'UI/components/molecules/HighlightBox';
import DecisionDialog from 'UI/components/organisms/DecisionDialog';
import EmptyPlaceholder from 'UI/components/templates/EmptyPlaceholder';
import FPDrawer from 'UI/components/templates/FPDrawer/FPDrawer';
import { Endpoints } from 'UI/constants/endpoints';
import { CompanyEntity, CompanyType, JobOrderEntity } from 'UI/constants/entityTypes';
import { Roles } from 'UI/constants/roles';
import { UIStatus } from 'UI/constants/status';
import { EmptyFeeAgreement } from 'UI/res';

import { JobOrderDetails } from '../../shared';

import { CONFIRMATION_DIALOGS, FORM_FIELDS_MAP } from './SendoutForm/SendoutForm.constants';
import { getSelectedAttachments } from './SendoutForm/SendoutForm.utils';
import { JOB_ORDER_INCLUDES } from './CreateSendoutDrawer.constants';
import { LoaderContainer } from './CreateSendoutDrawer.styled';
import {
  buildSendoutSubmissionData,
  getInterviewTimeValidationError
} from './CreateSendoutDrawer.utils';
import { SendoutForm } from './SendoutForm';

export const CreateSendoutDrawer = ({
  jobOrderId = null,
  isSubmitting = false,
  maxAttachmentsSize = DEFAULT_MAX_ATTACHMENTS_SIZE,
  onClose = () => {},
  onSaved = () => {},
  onViewDuplicates = () => {}
}) => {
  const history = useHistory();
  const form = useForm();
  const [currentUser] = useUserContext();

  const [candidateDuplicateInfo, setCandidateDuplicateInfo] = useState({
    isDuplicate: false,
    duplicates: [],
    error: null,
    shouldBlock: false
  });
  const [candidateInfo, setCandidateInfo] = useState(null);
  const [submissionData, setSubmissionData] = useState(null);
  const [activeDialog, setActiveDialog] = useState(null);
  const {
    candidateAttachments,
    hydrateCandidate,
    hydratedCandidateId,
    isHydratingCandidate,
    recruiterOptions
  } = useSendoutCandidateHydration({
    jobOrderId,
    onCandidateInfoChange: setCandidateInfo,
    onDuplicateChange: setCandidateDuplicateInfo
  });

  const { state } = useFetchWithStatusV2({
    endpoint: `${Endpoints.JobOrders}/${jobOrderId}?includes=${JOB_ORDER_INCLUDES}`,
    apiVersion: 2
  });

  const jobOrder = state.results || {};
  const { recruiter, coach, additionalRecruiters, company } = jobOrder;

  const isCompanySignedOrClient = [CompanyType.Client, CompanyType.Vendor].includes(
    company?.type?.id
  );

  const { canUserCreateSendout } = useSendoutCreationAccountability({
    recruiter,
    coach,
    additionalRecruiters
  });

  const canSkipHiringAuthorityEmail = userHasSomeRoles(currentUser, [Roles.Operations]);
  const canShowForm = canUserCreateSendout && isCompanySignedOrClient;

  const { clearError, handleSubmit, setError } = form;

  const isFetching = state.status === UIStatus.Loading;
  const hasErrorInInitialFetching = state.status === UIStatus.Error;
  const shouldShowCompanyNotSigned =
    canUserCreateSendout && state.status === UIStatus.Success && !isCompanySignedOrClient;

  const isFormVisible = canShowForm && state.status === UIStatus.Success;

  const sendoutInfo = {
    jobOrder: {
      id: jobOrder?.id,
      title: JobOrderEntity.singular,
      name: jobOrder?.title
    },
    company: {
      id: jobOrder?.company?.id,
      title: CompanyEntity.singular,
      name: jobOrder?.company?.name
    }
  };

  const closeConfirmationFlow = () => {
    setActiveDialog(null);
    setSubmissionData(null);
  };

  const handleClose = () => {
    if (isSubmitting) return;

    if (isFormVisible) {
      setActiveDialog(CONFIRMATION_DIALOGS.CloseForm);
      return;
    }

    onClose();
  };

  const handleConfirmDrawerClose = () => {
    setActiveDialog(null);
    onClose();
  };

  const closeFormActions = [
    {
      variant: 'outlined',
      text: strings.sendoutsV2.alerts.confirmationDialogs.closeForm.actions.discardAndExit,
      onClick: handleConfirmDrawerClose
    },
    {
      text: strings.formatString(
        strings.sendoutsV2.alerts.confirmationDialogs.closeForm.actions.cancel,
        { documentType: 'Sendout' }
      ),
      onClick: closeConfirmationFlow
    }
  ];

  const handleSave = formData => {
    const interviewTimeValidationError = getInterviewTimeValidationError(formData);

    if (interviewTimeValidationError) {
      setError(
        interviewTimeValidationError.fieldKey,
        'validate',
        interviewTimeValidationError.message
      );
      return;
    }

    clearError(FORM_FIELDS_MAP.InterviewFromHour.key);
    clearError(FORM_FIELDS_MAP.InterviewToHour.key);

    const selectedAttachments = getSelectedAttachments(formData);
    const attachmentsSizeError = getAttachmentsSizeValidationError(
      selectedAttachments,
      maxAttachmentsSize
    );

    if (attachmentsSizeError) {
      setError(FORM_FIELDS_MAP.Attachments.key, 'validate', attachmentsSizeError);
      return;
    }

    clearError(FORM_FIELDS_MAP.Attachments.key);

    const nextSubmissionData = buildSendoutSubmissionData({
      formData,
      jobOrder,
      candidateInfo
    });
    const hasNoAttachments = selectedAttachments.length === 0;

    setSubmissionData(nextSubmissionData);

    if (hasNoAttachments) {
      setActiveDialog(CONFIRMATION_DIALOGS.NoAttachments);
      return;
    }

    setActiveDialog(CONFIRMATION_DIALOGS.Preview);
  };

  const handleConfirmNoAttachments = () => {
    setActiveDialog(CONFIRMATION_DIALOGS.Preview);
  };

  const handleConfirmPreview = async () => {
    if (!submissionData) return;

    await onSaved(submissionData);
  };

  const handleViewCompanyProfile = () => {
    if (!company?.id) return;

    history.push(EntityRoutes.CompanyProfile.replace(':id', company.id));
  };

  const buttonDefinitions = [
    {
      props: {
        text: strings.shared.ui.cancel,
        variant: 'outlined',
        onClick: handleClose,
        disabled: isSubmitting
      },
      GridItemProps: { md: 3 }
    },
    {
      props: {
        text: strings.shared.ui.save,
        variant: 'contained',
        onClick: handleSubmit(handleSave),
        disabled:
          isFetching ||
          isHydratingCandidate ||
          candidateDuplicateInfo.shouldBlock ||
          isSubmitting ||
          hasErrorInInitialFetching,
        isSaving: isSubmitting
      },
      GridItemProps: { md: 3 },
      mode: 'save',
      visible: canShowForm
    }
  ];

  return (
    <>
      <FPDrawer
        FPDrawerProps={{ open: true, onClose: handleClose }}
        CardHeaderProps={{ title: strings.sendouts.tab.actions.createSendout }}
        onClose={handleClose}
        footer={
          <FPButtonBuilder
            GridContainerProps={{ spacing: 2 }}
            buttonDefinitions={buttonDefinitions}
          />
        }
      >
        <When condition={isFetching}>
          <LoaderContainer>
            <CircularProgress />
          </LoaderContainer>
        </When>
        <When condition={!isFetching}>
          <When condition={!hasErrorInInitialFetching && !canUserCreateSendout}>
            <EmptyPlaceholder
              subtitle={strings.formatString(strings.sendoutsV2.form.emptyPlaceholderText, {
                documentType: 'Sendout'
              })}
              height="100%"
            />
          </When>
          <When condition={hasErrorInInitialFetching}>
            <EmptyPlaceholder
              subtitle={strings.formatString(strings.sendoutsV2.form.initialFetchingError, {
                documentType: 'Sendout'
              })}
              height="100%"
            />
          </When>

          <When condition={shouldShowCompanyNotSigned}>
            <EmptyPlaceholder
              title={strings.sendoutsV2.alerts.noSignedCompany.title}
              subtitle={strings.sendoutsV2.alerts.noSignedCompany.description}
              customEmptyState={<EmptyFeeAgreement />}
            >
              <FPActionButton
                text={strings.sendoutsV2.alerts.noSignedCompany.signCompany}
                type="button"
                onClick={handleViewCompanyProfile}
              />
            </EmptyPlaceholder>
          </When>

          <When condition={isFormVisible}>
            <FormContext {...form}>
              <JobOrderDetails company={sendoutInfo.company} jobOrder={sendoutInfo.jobOrder} />
              <SendoutForm
                candidateAttachments={candidateAttachments}
                candidateHydratedId={hydratedCandidateId}
                candidateInfo={candidateInfo}
                canSkipHiringAuthorityEmail={canSkipHiringAuthorityEmail}
                duplicateInfo={candidateDuplicateInfo}
                hydrateCandidate={hydrateCandidate}
                isHydratingCandidate={isHydratingCandidate}
                jobOrder={jobOrder}
                recruiterOptions={recruiterOptions}
                onViewDuplicates={onViewDuplicates}
              />
            </FormContext>
          </When>
        </When>
      </FPDrawer>

      <When condition={activeDialog === CONFIRMATION_DIALOGS.NoAttachments}>
        <DecisionDialog
          cancelButtonText={strings.shared.ui.cancel}
          confirmButtonText={strings.shared.ui.send}
          isOpened
          onClose={closeConfirmationFlow}
          onConfirm={handleConfirmNoAttachments}
          severity="warning"
          showCloseButton={false}
          showSeverityIcon
          title={strings.sendoutsV2.alerts.confirmationDialogs.noAttachments.title}
          withButtons="YesNo"
          message={strings.sendoutsV2.alerts.confirmationDialogs.noAttachments.message}
        />
      </When>
      <When condition={activeDialog === CONFIRMATION_DIALOGS.Preview}>
        <DecisionDialog
          cancelButtonText={strings.shared.ui.cancel}
          confirmButtonText={strings.shared.ui.singleConfirm}
          isOpened
          mustComplete
          fetching={isSubmitting}
          onClose={closeConfirmationFlow}
          onConfirm={handleConfirmPreview}
          severity="info"
          showCloseButton={false}
          showSeverityIcon
          title={strings.sendoutsV2.alerts.confirmationDialogs.emailConfirmation.title}
          message={strings.sendoutsV2.alerts.confirmationDialogs.emailConfirmation.message}
          withButtons="YesNo"
        >
          <HighlightBox
            text={submissionData?.confirmation?.resolvedEmailBody || ''}
            highlightData={submissionData?.confirmation?.resolvedTagValues || []}
          />
        </DecisionDialog>
      </When>
      <When condition={activeDialog === CONFIRMATION_DIALOGS.CloseForm}>
        <DecisionDialog
          actions={closeFormActions}
          isOpened
          message={strings.formatString(
            strings.sendoutsV2.alerts.confirmationDialogs.closeForm.message,
            { documentType: 'Sendout' }
          )}
          onClose={closeConfirmationFlow}
          showSeverityIcon
          title={strings.formatString(
            strings.sendoutsV2.alerts.confirmationDialogs.closeForm.title,
            { documentType: 'Sendout' }
          )}
        />
      </When>
    </>
  );
};
